import { ApiUtils } from 'api/ApiUtils';
import axios, { AxiosRequestConfig } from 'axios';
import * as R from 'ramda';
import { SDKLogger } from 'services/system/SDKLogger';
import { BackendType } from 'utils/DPP/DPP.types';
import { JsonSafe } from 'utils/JsonSafe';
import { RandomHelper } from 'utils/RandomHelper';
import xml2js from 'xml-js';

import { BoundCsrfTokenSelectrors } from 'reactApp/modules/CsrfTokenModule/CsrfTokenModule.selectors';

const API_VERSION = 1;

const API_PREFIX = `/api/v${API_VERSION}/`;

const _handleUrl = (url) => {
    const openstackUrlRegex = /^\/infra\/.+$/;
    const consistPrefixRegex = /^\/api\/v[^/]+.+$/;
    const fullUrlRegex = /^https?:\/\/.+$/;

    if (
        [openstackUrlRegex, consistPrefixRegex, fullUrlRegex].some((regex) =>
            regex.test(url),
        )
    ) {
        return url;
    }
    return `${API_PREFIX}${_removeLeadingSlash(url)}`;
};

const _removeLeadingSlash = (url) => url.replace(/^\/(.+)/, '$1');

type Response<T> = {
    data: T;
};

type ArgTypeCommon = {
    url: string;
    method?: HttpMethods;
    data?: Record<string, any>;
    apiVersioning?: boolean;
    clearEmptyValues?: boolean;
    noCache?: boolean;
    options?: AxiosRequestConfig;
    noCsrfToken?: boolean;
    withCredentials?: boolean;
    backendType?: BackendType;
};

type ArgType1 = ArgTypeCommon & {
    returnRawResponse?: false;
};

type ArgType2 = ArgTypeCommon & {
    returnRawResponse: true;
};
async function rpcCall<Body>(arg: ArgType1): Promise<Body>;
async function rpcCall<Body>(arg: ArgType2): Promise<Response<Body>>;
async function rpcCall(
    arg: ArgType2 | ArgType1,
): Promise<Response<Body> | Body> {
    const {
        url,
        method = 'GET',
        returnRawResponse,
        apiVersioning = true,
        clearEmptyValues,
        options,
        noCache,
        noCsrfToken,
        backendType,
        ...config
    } = arg;

    const handledUrl = apiVersioning ? _handleUrl(url) : url;
    const { baseURL } = axios.defaults;
    if (!apiVersioning) {
        axios.defaults.baseURL = '/';
    }
    let { data } = arg;
    if (clearEmptyValues && data) {
        data = R.reject(
            (value: any) => ['', null, undefined].includes(value),
            data,
        );
    }

    const responseType = options?.responseType || 'json';
    const _options = getOptions({
        method,
        responseType,
        url: handledUrl,
        data,
    });

    const response = await call<{ data: Body }>(
        {
            ..._options,
            ...config,
            url: noCache ? getUrlWithSuffix(_options.url) : _options.url,
            headers: options?.headers || {},
        },
        noCsrfToken,
        backendType,
    );
    if (!apiVersioning) {
        axios.defaults.baseURL = baseURL;
    }
    return returnRawResponse ? response : response.data;
}

type GetOptionsParams = {
    method: HttpMethods;
    responseType: AxiosRequestConfig['responseType'];
    url: string;
    data?: Record<string, string | number>;
};
function getOptions({ method, responseType, url, data }: GetOptionsParams) {
    if (method === 'GET' && data) {
        return {
            url: url + getQueryParamsFromData(data),
            method,
            responseType,
        };
    }

    return {
        url,
        method,
        data: ['POST', 'PUT'].includes(method) ? data || {} : data,
        responseType,
    };
}

function getQueryParamsFromData(data: Record<string, string | number>) {
    return Object.entries(data).reduce((queryParams, [key, value], index) => {
        const separator = index === 0 ? '?' : '&';
        return `${queryParams}${separator}${key}=${value}`;
    }, '');
}

function getUrlWithSuffix(url: string) {
    const suffix = RandomHelper.getRandomInt(1000000000000, 9999999999999);
    return url.includes('?') ? `${url}&_=${suffix}` : `${url}?_=${suffix}`;
}

const loadText = async (url, data = {}) => {
    return rpcCall<string>({
        url,
        options: {
            responseType: 'text',
        },
        data,
        apiVersioning: false,
        noCache: true,
    });
};

const loadXml = async <T>(url, data = {}) => {
    const xml = await rpcCall<string>({
        url,
        options: {
            responseType: 'text',
        },
        data,
        apiVersioning: false,
        noCache: true,
    });

    return xml2js.xml2js(xml, { compact: true }) as T;
};

function call<T>(
    { responseType, method, url, headers, data, ...config }: AxiosRequestConfig,
    noCsrfToken = false,
    backendType?: BackendType,
): Promise<T> {
    const shouldAddDefaultJsonHandling =
        !responseType && method?.toLowerCase() !== 'get';

    if (shouldAddDefaultJsonHandling) {
        responseType = 'json';
    }

    const token = BoundCsrfTokenSelectrors.getToken();

    if (token) {
        headers = { ...headers, 'x-csrf-token': token };
    }

    headers = { ...headers, 'Cache-Control': 'no-cache' };

    return _callWithLogging<T>(
        {
            method,
            data,
            url,
            responseType,
            headers,
            ...config,
        },
        noCsrfToken,
        backendType,
    );
}

function _callWithLogging<T>(
    { method, data, url, headers, ...config }: AxiosRequestConfig,
    noCsrfToken = false,
    backendType,
): Promise<T> {
    const requestLogger = SDKLogger.createSingleRequestLogger({
        group: 'api',
        method,
        url,
        logResponseTextOnError: true,
        backendType,
    });

    headers = {
        ...headers,
        ...requestLogger.getLoggingHeaders(),
    };

    if (noCsrfToken) {
        delete headers[ApiUtils.HEADER_KEYS.X_CSRF_TOKEN];
    }

    const originalResult = axios({
        url,
        method,
        data,
        headers,
        ...config,
    });

    return originalResult.then(_logOnResponse, _logOnError);

    function _logOnResponse(response) {
        const xhr = response.request;
        requestLogger.logXhrOnResponse({
            xhr,
            customData: {
                aborted: xhr.aborted,
            },
        });

        return response;
    }

    function _logOnError(response) {
        const xhr = response.request;
        requestLogger.logXhrOnResponse({
            xhr,
            customData: {
                aborted: xhr.aborted,
            },
        });
        return Promise.reject(JsonSafe.parse(xhr.response) || xhr.response);
    }
}

function simpleXmlToJson(node) {
    let result = {};

    if (node.nodeType === Node.TEXT_NODE) {
        result = node.nodeValue;
    } else if (node.hasChildNodes()) {
        for (
            let i = 0,
                childNodes = node.childNodes,
                childNodeCount = childNodes.length;
            i < childNodeCount;
            i++
        ) {
            const childNode = childNodes.item(i);
            const childNodeName = childNode.nodeName;
            const childNodeType = childNode.nodeType;

            if (result.hasOwnProperty(childNodeName)) {
                if (!Array.isArray(result[childNodeName])) {
                    result[childNodeName] = [result[childNodeName]];
                }

                result[childNodeName].push(simpleXmlToJson(childNode));
            } else if (childNodeType === Node.ELEMENT_NODE) {
                result[childNodeName] = simpleXmlToJson(childNode);
            } else {
                result = simpleXmlToJson(childNode);
            }
        }
    }

    return result;
}

export const ApiHelper = {
    loadText,
    loadXml,
    rpcCall,
    simpleXmlToJson,
};
